import { useMutation, useQuery } from '@apollo/client';
import { graphql, useStaticQuery } from 'gatsby';
import { descend, head, prop, reduce, sort } from 'ramda';
import React, { FC, useMemo, useState } from 'react';

import { keysToCamel } from '@utils/butter';
import { exists } from '@utils/general';

import { ConsentType, ICurrentPolicy, ICurrentPolicyNode } from '@data/consent';

import ACCEPT_POLICY from '@graphql/mutations/recordAcceptance.mutation.gql';
import GET_CURRENT_POLICY from '@graphql/queries/getCurrentPolicy.query.gql';

import TermsOfServiceDialog, {
	ITermsOfServiceDialogContent,
} from './TermsOfServiceDialog.component';

const TermsOfServiceDialogWrapper: FC = () => {
	const [showDialog, setShowDialog] = useState(true);

	const { dialogContent } = useStaticQuery(graphql`
		{
			dialogContent: butterPage(slug: { eq: "terms-of-service-dialog" }) {
				tos_header
				tos_description
				tos_disclaimer
				tos_support
				agree_label
				disagree_label
			}
		}
	`);

	const { data } = useQuery<ICurrentPolicy>(GET_CURRENT_POLICY, {
		fetchPolicy: 'no-cache',
		variables: {
			consentType: ConsentType.TERMS_OF_SERVICE,
		},
	});

	const refinedContent: ITermsOfServiceDialogContent = keysToCamel(
		dialogContent
	) as ITermsOfServiceDialogContent;

	const consentPolicy = useMemo(
		() =>
			reduce(
				(latestNode: ICurrentPolicyNode, comparisonNode: ICurrentPolicyNode) => {
					const nodes = [latestNode, comparisonNode];
					const latestVersion = descend<ICurrentPolicyNode>(prop('version'));
					return head(sort(latestVersion, nodes)) ?? { version: 0, contentSummary: '' };
				},
				{ version: 0, contentSummary: '' },
				data?.getCurrentPolicy?.nodes ?? []
			),
		[data?.getCurrentPolicy?.nodes]
	);

	const [acceptPolicy] = useMutation(ACCEPT_POLICY, {
		variables: {
			consentType: ConsentType.TERMS_OF_SERVICE,
			version: consentPolicy?.version,
		},
	});

	const onAgree = () => {
		acceptPolicy().then(() => {
			setShowDialog(false);
		});
	};

	return (
		showDialog &&
		exists(consentPolicy?.contentSummary) && (
			<TermsOfServiceDialog
				dialogContent={refinedContent}
				consentPolicy={consentPolicy}
				onAgree={onAgree}
			/>
		)
	);
};

export default TermsOfServiceDialogWrapper;
